import { useMemo } from 'react'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'
import { GPK_PEGAWAI_TUGAS_POKOK } from 'app/KinerjaGpkPage/constants/contents'
import {
  useAPIContentKinerjaUtama,
  useAPIContentPerilakuKerja,
} from 'api/kinerja/useAPIKinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { MultiPeriodPayload } from 'types/kinerja'
import type { ResolvedPerencanaanData } from 'app/KinerjaGpkPage/types/perencanaan'

export type PerencanaanPegawaiDetailProps = {
  isLoading: boolean
  periodParams?: MultiPeriodPayload
  data?: ResolvedPerencanaanData
}

export function PerencanaanPegawaiDetail({
  isLoading,
  periodParams,
  data = {},
}: PerencanaanPegawaiDetailProps) {
  const { periodYear, periodNo } = usePeriodContext()

  // -----------------------------------------
  // Get content data for mapping
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'GPK',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const {
    data: dataContentPerilakuKerja,
    isLoading: isLoadingContentPerilakuKerja,
  } = useAPIContentPerilakuKerja(
    {
      tipe: 'GPK',
      periodParams: periodParams
        ? {
            // Use period params if provided
            skp_year: periodParams.skpYear,
            skp_period: periodParams.skpPeriod,
          }
        : {
            // Use current period as fallback
            skp_year: periodYear,
            skp_period: periodNo,
          },
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  // -----------------------------------------
  // Map data to content
  // -----------------------------------------
  const tugasTambahan = useMemo(() => {
    if (!data.tugasTambahanId) return null

    const content = dataContentKinerjaUtama?.data?.tugasTambahan?.find(
      (item) => item.id === data.tugasTambahanId
    )?.rencanaHasilKerja

    return content
  }, [data?.tugasTambahanId, dataContentKinerjaUtama])

  const praktikKinerja = useMemo(() => {
    if (!data.praktikKinerjaId) return null

    // The praktik kinerja is the first indikator per dimensi
    const content = dataContentKinerjaUtama?.data?.praktikPembelajaran?.find(
      (item) => item.indikator?.[0]?.id === data.praktikKinerjaId
    )?.indikator?.[0]?.nama

    return content
  }, [data.praktikKinerjaId, dataContentKinerjaUtama])

  const pengembanganKompetensi = useMemo(() => {
    if (!data.pengembanganKompetensiId) return null

    const content = dataContentKinerjaUtama?.data?.pengembanganKompetensi?.find(
      (item) => item.id === data.pengembanganKompetensiId
    )?.rencanaHasilKerja

    return content
  }, [data.pengembanganKompetensiId, dataContentKinerjaUtama])

  const perilakuKerjaRows = useMemo(() => {
    const perilakuKerja = data.perilakuKerjaItems || []
    const aspekList = dataContentPerilakuKerja?.data?.aspek || []

    return perilakuKerja.map((pk) => {
      const aspek = aspekList.find((a) => a.id === pk.aspekId)
      const indikator = aspek?.indikator.find((i) => i.id === pk.indikatorId)
      const ekspektasi = indikator?.ekspektasi.find(
        (e) => e.id === pk.ekspektasiId
      )

      return {
        aspek: aspek?.value || '',
        indikator: indikator?.value || '',
        ekspektasi: ekspektasi?.value || '',
      }
    })
  }, [data.perilakuKerjaItems, dataContentPerilakuKerja])

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingAll =
    isLoading || isLoadingContentKinerjaUtama || isLoadingContentPerilakuKerja

  if (isLoadingAll) {
    return (
      <div className="space-y-9">
        <Shimmer height={320} width="100%" className="rounded" />
        <Shimmer height={128} width="100%" className="rounded" />
        <Shimmer height={128} width="100%" className="rounded" />
        <Shimmer height={440} width="100%" className="rounded" />
      </div>
    )
  }

  return (
    <div className="space-y-9">
      {/* Pelaksanaan Tugas Pokok */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm" as="h3">
            Pelaksanaan Tugas Pokok
          </Text>
        </div>
        <div className="space-y-4">
          {GPK_PEGAWAI_TUGAS_POKOK.map((tugasPokok, index) => (
            <IndikatorPerencanaanPanel
              key={`tugas-pokok-${index}`}
              variant="static"
              orderNumber={index + 1}
              title={tugasPokok}
            />
          ))}
          <IndikatorPerencanaanPanel
            variant="static"
            orderNumber={GPK_PEGAWAI_TUGAS_POKOK.length + 1}
          >
            <Text>
              Terlaksananya penugasan tambahan dalam mendukung layanan
              pembelajaran bagi murid penyandang disabilitas GPK berdasarkan
              pilihan <strong>{tugasTambahan}</strong>
            </Text>
          </IndikatorPerencanaanPanel>
        </div>
      </section>

      {/* Praktik Kinerja */}
      {praktikKinerja && (
        <section>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={assetUrl('/illustrations/img-badge-star.png')}
              className="flex-shrink-0 flex-grow-0"
              alt=""
              width={52}
              height={52}
            />
            <Text variant="heading-sm">Praktik Kinerja</Text>
          </div>
          <IndikatorPerencanaanPanel variant="static" orderNumber={1}>
            <Text>
              Terlaksananya praktik pembelajaran dan/atau
              pembimbingan/pendampingan melalui observasi praktik kinerja yang
              disepakati bersama Kepala Dinas yang berfokus pada
              <strong> {praktikKinerja}</strong>
            </Text>
          </IndikatorPerencanaanPanel>
        </section>
      )}

      {/* Pengembangan Kompetensi */}
      {pengembanganKompetensi && (
        <section>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={assetUrl('/illustrations/img-document.png')}
              className="flex-shrink-0 flex-grow-0"
              alt=""
              width={52}
              height={52}
            />
            <Text variant="heading-sm">Pengembangan Kompetensi</Text>
          </div>
          <IndikatorPerencanaanPanel variant="static" orderNumber={1}>
            <Text>
              Terlaksananya pengembangan kompetensi dalam meningkat kan layanan
              pembelajaran bagi murid penyandang disabilitas dan/atau
              peningkatan karier berdasarkan indikator
              <strong> {pengembanganKompetensi}</strong>
            </Text>
          </IndikatorPerencanaanPanel>
        </section>
      )}

      {/* Perilaku Kerja */}
      {perilakuKerjaRows.length > 0 && (
        <section>
          <Text variant="heading-sm" className="mb-4">
            Perilaku Kerja
          </Text>
          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem className="w-12">No.</TableHeaderItem>
              <TableHeaderItem>Aspek</TableHeaderItem>
              <TableHeaderItem>Indikator Perilaku</TableHeaderItem>
              <TableHeaderItem>Ekspektasi Khusus Pimpinan</TableHeaderItem>
            </TableHeader>
            <TableBody>
              {perilakuKerjaRows.map((row, index) => (
                <TableRow key={`perilaku-kerja-${index}`}>
                  <TableRowItem>{index + 1}</TableRowItem>
                  <TableRowItem>{row.aspek}</TableRowItem>
                  <TableRowItem>{row.indikator}</TableRowItem>
                  <TableRowItem>{row.ekspektasi}</TableRowItem>
                </TableRow>
              ))}
            </TableBody>
          </TablePelaksanaanKinerja>
        </section>
      )}
    </div>
  )
}
